"use client";

import Script from "next/script";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useIdentity } from "@/lib/api/session";
import type { SmarticoConfig } from "./config";
import type { SmarticoApi } from "./types";

/* Loading Smartico, and telling it who the player is.

   Two jobs, and they happen in that order. The script is fetched and
   `_smartico.init` is called with the label and brand keys; then, once a
   session exists, the account id is handed over and Smartico identifies the
   player.

   `_smartico.api` is present as soon as init returns, before anyone is
   identified — so its existence is not the readiness signal it looks like.
   What this publishes is the `identify` event instead: an api bound to nobody
   would answer for nobody, and the widgets would query on behalf of a visitor
   who has no missions and no level.
   docs/findings/smartico-api-exists-before-identify.md

   With no label configured none of this renders and `api` stays null. Every
   consumer falls back on its own. */

const IDENTIFY_RETRY_MS = 3_000;

type SmarticoState = {
  /* Non-null only when the script is loaded *and* a player is identified. */
  api: SmarticoApi | null;
  /* Whether the integration is configured at all, which is a different
     question from whether it is ready — a configured-but-signed-out visitor is
     `isEnabled` and has no `api`. */
  isEnabled: boolean;
};

const SmarticoContext = createContext<SmarticoState>({ api: null, isEnabled: false });

export const useSmartico = () => useContext(SmarticoContext);

/* Deep links into Smartico's own dashboard. There is no page of ours behind
   either widget, and building one would mean reimplementing a dashboard the
   provider already ships. */
export const MISSIONS_DEEP_LINK = "dp:gf_missions";
export const LEVELS_DEEP_LINK = "dp:gf_levels";

export const openSmartico = (deepLink: string) => {
  window._smartico?.dp(deepLink);
};

export function SmarticoProvider({
  config,
  children,
}: {
  config: SmarticoConfig | null;
  children: ReactNode;
}) {
  const { identity } = useIdentity();
  const accountId = identity?.id ?? null;

  const [isLoaded, setIsLoaded] = useState(false);
  const [api, setApi] = useState<SmarticoApi | null>(null);

  /* The globals have to exist before the vendor script runs, and an effect is
     the only place we can set them without an inline script — so the <Script>
     is withheld for one render rather than racing it. Costs a tick; keeps
     `dangerouslySetInnerHTML` out of the app entirely. */
  const [globalsReady, setGlobalsReady] = useState(false);

  useEffect(() => {
    if (!config) return;

    /* The vendor's own escape hatch for local development. Whether a given
       label restricts origins at all is set on Smartico's side, not ours — the
       whitelabel's development label accepts a *.wagmicasino.com origin as it
       stands (docs/findings/smartico-inits-from-our-origin-already.md), which
       is why it no longer ships as the default. This flag only ever concerns
       localhost. */
    if (window.location.hostname.includes("localhost")) {
      window._smartico_allow_localhost = true;
    }

    window._smartico_language = "en";
    setGlobalsReady(true);
  }, [config]);

  /* `identify` is what publishes `_smartico.api`, and it can arrive before this
     component subscribes — a warm bfcache restore, or a second mount. So the
     handler is registered *and* the global is checked once on the way in. */
  useEffect(() => {
    if (!config || !isLoaded) return;

    const smartico = window._smartico;

    if (!smartico) return;

    const publish = () => {
      if (window._smartico?.api) {
        setApi(window._smartico.api);
      }
    };

    publish();
    smartico.on("identify", publish);

    return () => smartico.off("identify", publish);
  }, [config, isLoaded]);

  /* Who is playing. Signing out clears the id and drops the api, so a hook that
     is still mounted stops reading the previous player's missions.

     `logout` ends a session that existed. Calling it for a visitor who never
     signed in makes Smartico log "cannot call _smartico.logout before
     _smartico.identify" — which was every signed-out page load, in everyone's
     console, reporting a fault that was ours.
     docs/findings/smartico-logout-before-identify.md */
  useEffect(() => {
    if (!config || !isLoaded || !window._smartico) return;

    if (accountId === null) {
      window._smartico_user_id = null;
      setApi(null);
      return;
    }

    window._smartico_user_id = accountId;
    window._smartico.online(accountId);
  }, [config, isLoaded, accountId]);

  /* Smartico drops an identify that races an in-flight logout and never retries
     it, which leaves a signed-in player unidentified until a full reload — the
     quick sign-out-sign-in path hits it every time. Keep asking until the api
     shows up. */
  useEffect(() => {
    if (!config || !isLoaded || accountId === null || api !== null) return;

    const retry = window.setInterval(() => {
      window._smartico?.online(accountId);
    }, IDENTIFY_RETRY_MS);

    return () => window.clearInterval(retry);
  }, [config, isLoaded, accountId, api]);

  /* Smartico can draw its own inbox and popups over the page. The site already
     owns its chrome — a vendor overlay appearing on top of the lobby is the
     problem `docs/findings/betby-paints-over-the-app-chrome.md` records for the
     other embedded provider. Both are suspended; the dashboard is still
     reachable from the widgets, which is the deliberate way in. */
  useEffect(() => {
    if (api === null || !window._smartico) return;

    window._smartico.suspendInbox(true);
    window._smartico.suspendPopups(true);
  }, [api]);

  const state = useMemo(
    () => ({ api, isEnabled: config !== null }),
    [api, config],
  );

  return (
    <SmarticoContext.Provider value={state}>
      {config && globalsReady && (
        <Script
          id="smartico-sdk"
          src={config.scriptUrl}
          strategy="afterInteractive"
          onLoad={() => {
            window._smartico?.init(config.labelKey, { brand_key: config.brandKey });
            setIsLoaded(true);
          }}
          /* A gamification widget is not worth a broken page. The failure is
             silent by design: the widgets fall back to what the platform can
             answer, which is what an unconfigured deployment shows anyway. */
          onError={() => setIsLoaded(false)}
        />
      )}
      {children}
    </SmarticoContext.Provider>
  );
}
